import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcProspectusV1ProspectusJobQueryServiceProspectusJobGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcProspectusV1ProspectusJobState } from '@interxfi/types';
import { buildTitledTableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import { ProspectusJobEventDrawer, ProspectusJobEventSummary } from '../../../../../../../../../../features/prospectus/job/prospectus-job-event';
import { getProspectusJobStateListItems } from '../../../../../../../../../../features/prospectus/job/prospectus-job-list-items';
import { getProspectusJobDetailPath } from '../../../../../../../../../../features/prospectus/job/prospectus-job-routing';
import { ProspectusJobStatusPill } from '../../../../../../../../../../features/prospectus/job/prospectus-job-status-pill';
import { ProspectusTasksTable } from '../../../../../../../../../../features/prospectus/task/prospectus-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/beta-nxt/prospectus-job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `prospectus-job-${params.jobId}`,
        label: 'Job',
        to: getProspectusJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Prospectus Job'),
  component: ProspectusJobDetail,
});

function getListItems(data: IxcProspectusV1ProspectusJobState | undefined) {
  const items = getProspectusJobStateListItems(data);

  return {
    left: [
      items.data.spec.tradeDate,
      { ...items.data.metrics.total, title: 'Total Tasks' },
      { ...items.data.metrics.pending, title: 'Pending Tasks' },
      { ...items.data.metrics.succeeded, title: 'Succeeded Tasks' },
      { ...items.data.metrics.committed, title: 'Committed Tasks' },
      { ...items.data.metrics.errored, title: 'Errored Tasks' },
      { ...items.data.metrics.failed, title: 'Failed Tasks' },
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.jobId] as const satisfies ReadonlyListItem[],
  } as const;
}

function getEntityMenu(jobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: ProspectusJobEventDrawer,
        props: { request: { jobId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load prospectus job', 'view this prospectus job');

const tasksTableHeader = buildTitledTableSectionHeader('Tasks');

function ProspectusJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jobId } as const;
  const { data, error, isPending } = useIxcProspectusV1ProspectusJobQueryServiceProspectusJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const menuItems = useMemo(() => getEntityMenu(jobId), [jobId]);

  const listItems = useMemo(() => getListItems(data?.prospectusJob), [data?.prospectusJob]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <ProspectusJobStatusPill isPending={showPending} size="large" status={data?.prospectusJob?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <ProspectusJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <ProspectusTasksTable HeaderActions={tasksTableHeader} jobId={jobId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
